/**
 * Human Lithuanian texts for transport error codes (spec-web-ui §2:
 * "klaidos — registro kodai, rodomi žmogui suprantamai"). The registry
 * code always rides along in the status area for traceability (the
 * codes-need-gloss rule, inverted: gloss first, code kept). Pure map,
 * unit-tested in test/ui/errors-lt.test.ts; unknown codes degrade to a
 * generic text WITH the code visible, never a blank.
 */

const LT_BY_CODE: Record<string, string> = {
  // Session / permissions
  unauthorized: "Sesija baigėsi — prisijunkite iš naujo.",
  invalid_credentials: "Neteisingas el. paštas arba slaptažodis.",
  insufficient_scope: "Jūsų paskyra turi tik skaitymo teises.",
  rate_limited: "Per daug bandymų — pabandykite vėliau.",
  origin_rejected: "Užklausa atmesta saugumo sumetimais — perkraukite puslapį.",
  // Lookups
  not_found: "Įrašas nerastas.",
  client_not_found: "Klientas nerastas.",
  project_not_found: "Projektas nerastas.",
  artifact_not_found: "Artefaktas nerastas.",
  version_not_found: "Versija nerasta.",
  // Check-out lock / base-version conflicts (spec-artifact-locking §7).
  // These are FALLBACKS: when the error carries its details, the status
  // strip shows the richer text ui/src/lib/locking.ts composes from them
  // (who holds the file, since when, which version won). The generic form
  // must still be true on its own — an older server, a proxy that dropped
  // the details, or a shape this bundle does not recognise lands here.
  lock_held: "Failą darbui jau paėmė kitas darbuotojas.",
  artifact_locked: "Failą darbui yra paėmęs kitas darbuotojas — įkelti negalima.",
  // The next step is a FRESH TAKE, not a bare retry: a failed return keeps
  // the remembered base (ui/src/lib/base-memory.ts), so re-sending the same
  // file would declare the same stale version and be refused again.
  version_conflict: `Kol dirbote, failas buvo atnaujintas — atsisiųskite dabartinę versiją, perkelkite pakeitimus ir paimkite failą darbui iš naujo („Paimti darbui"); tik tada bandykite dar kartą.`,
  // Requests
  validation_failed: "Užklausa atmesta — patikrinkite įvestį.",
  payload_too_large: "Failas per didelis — viršija leidžiamą dydį.",
  download_link_invalid: "Atsisiuntimo nuoroda negalioja.",
  download_link_expired: "Atsisiuntimo nuoroda pasibaigė — sugeneruokite naują.",
  // Client-side codes (ui/src/transport/types.ts ClientErrorCode)
  network_error: "Nepavyko pasiekti serverio — patikrinkite ryšį.",
  not_implemented: "Ši funkcija dar neįdiegta.",
  upload_aborted: "Įkėlimas atšauktas.",
  action_aborted: "Veiksmas atšauktas.",
  artifact_name_ambiguous:
    "Projekte yra keli artefaktai tokiu pačiu vardu — pervadinkite failą ir bandykite dar kartą.",
  upload_plan_changed:
    "Kol ruošėte įkėlimą, tokiu pačiu vardu esantis failas projekte pasikeitė — pasirinkite failą iš naujo ir patvirtinkite.",
  internal_error: "Serverio klaida — pabandykite dar kartą.",
  // SketchUp bridge codes (ui/src/transport/sketchup.ts; see ui/BRIDGE.md)
  bridge_unavailable: "Nėra ryšio su SketchUp — atidarykite langą per SketchUp įskiepį.",
  bridge_contract_mismatch: "Nesuderinama dialogo ir įskiepio versija — atnaujinkite įskiepį.",
  disposed: "Langas uždarytas — operacija nutraukta.",
  // Raised BOTH UI-side (the getModelInfo pre-check) and by the host's
  // upload.perform reject (spec-upload-unification §5 double protection).
  model_not_saved: "Išsaugokite modelį prieš įkeldami.",
};

export function ltErrorText(code: string): string {
  return LT_BY_CODE[code] ?? "Nenumatyta klaida.";
}

/** TEST SEAM: the mapped codes, so the registry-tie test asserts against
 * the real map instead of a hand-copied list (test/ui/errors-lt.test.ts). */
export function mappedErrorCodes(): string[] {
  return Object.keys(LT_BY_CODE);
}
